import { TextColor } from '../../../Atoms/Text';
import { ClientScore, ScoringCategory, ScoringSupplier, SupplierRating } from '../../../../types/advise';
import { StepProgress, StepStatus } from '../EngagementSteps';

export const STRONG_SCORE = 76;

export type ScoreTone = 'strong' | 'caution' | 'unrated';

export const SCORE_COLORS: Record<ScoreTone, TextColor> = {
  strong: 'green600',
  caution: 'amber',
  unrated: 'grey6'
};

export const scoreTone = (score: number | null): ScoreTone => {
  if (score === null) return 'unrated';
  return score >= STRONG_SCORE ? 'strong' : 'caution';
};

const average = (values: number[]): number | null =>
  values.length === 0 ? null : Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);

export const ratingKey = (supplierId: string, functionId: string) => `${supplierId}:${functionId}`;

export const ratingsByKey = (ratings: SupplierRating[]): Map<string, number> =>
  new Map(ratings.map((row) => [ratingKey(row.supplier_id, row.function_id), row.rating]));

export const ratingRowsByKey = (ratings: SupplierRating[]): Map<string, SupplierRating> =>
  new Map(ratings.map((row) => [ratingKey(row.supplier_id, row.function_id), row]));

export const clientScoresByKey = (clientScores: ClientScore[]): Map<string, ClientScore> =>
  new Map(clientScores.map((row) => [ratingKey(row.supplier_id, row.function_id), row]));

// What the table's score column shows: the clients' average, or the user's own rating.
export type ScoringView = 'client' | 'mine';

// Client input is purple wherever it shows; green and amber stay the user's own rating.
export const CLIENT_PURPLE = '#9333ea';

export const WIDE_SPREAD = 30;

// The longest note upstack_api's Engagements::SupplierScoringSchemas::RATING takes.
export const ADJUSTMENT_NOTE_LIMIT = 2000;

export const scoreSpread = (clientScore: ClientScore): number => {
  const scores = clientScore.scores.map((row) => row.score);
  return Math.max(...scores) - Math.min(...scores);
};

export const isWideSpread = (clientScore?: ClientScore): boolean =>
  !!clientScore && scoreSpread(clientScore) >= WIDE_SPREAD;

// Worked out from the current average, so a new client score can add or clear it.
export const isAdjusted = (rating: number | undefined, clientScore?: ClientScore): boolean =>
  rating !== undefined && !!clientScore && rating !== clientScore.average;

export const responsesLabel = (count: number): string => (count === 1 ? '1 response' : `${count} responses`);

export interface CategoryClientScore {
  withResponses: number;
  total: number;
  // The average of its functions' averages, rounded; null when no client scored any.
  average: number | null;
}

export const categoryClientScore = (
  category: ScoringCategory,
  supplierId: string,
  clientScores: Map<string, ClientScore>
): CategoryClientScore => {
  const averages = category.functions.flatMap((fn) => clientScores.get(ratingKey(supplierId, fn.id))?.average ?? []);
  return { withResponses: averages.length, total: category.functions.length, average: average(averages) };
};

export interface CategoryScore {
  rated: number;
  total: number;
  // Rounded; null with nothing rated, which is never the same as 0.
  average: number | null;
}

export const categoryScore = (
  category: ScoringCategory,
  supplierId: string,
  ratings: Map<string, number>
): CategoryScore => {
  const given = category.functions.flatMap((fn) => ratings.get(ratingKey(supplierId, fn.id)) ?? []);
  return { rated: given.length, total: category.functions.length, average: average(given) };
};

// What a supplier's scores are read from: the clients' averages, the score of record, or the user's own ratings.
export interface SupplierScores {
  view: ScoringView;
  ratings: Map<string, number>;
  clientScores: Map<string, ClientScore>;
}

export const categoryAverage = (
  category: ScoringCategory,
  supplierId: string,
  { view, ratings, clientScores }: SupplierScores
): number | null =>
  view === 'client'
    ? categoryClientScore(category, supplierId, clientScores).average
    : categoryScore(category, supplierId, ratings).average;

// The average of the supplier's categories that have a score, so one with none doesn't pull it down.
export const overallScore = (
  categories: ScoringCategory[],
  supplierId: string,
  scores: SupplierScores
): number | null => average(categories.flatMap((category) => categoryAverage(category, supplierId, scores) ?? []));

export const ratingProgress = (
  categories: ScoringCategory[],
  supplierIds: string[],
  ratings: Map<string, number>
): StepProgress => {
  const functionIds = categories.flatMap((category) => category.functions.map((fn) => fn.id));
  const answered = supplierIds.reduce(
    (count, supplierId) => count + functionIds.filter((id) => ratings.has(ratingKey(supplierId, id))).length,
    0
  );
  return { answered, required: functionIds.length * supplierIds.length, item: 'function' };
};

export const supplierScoringStepStatus = (suppliers: ScoringSupplier[], progress: StepProgress): StepStatus => {
  if (suppliers.length > 0 && suppliers.every((supplier) => supplier.status === 'completed')) return 'complete';
  return progress.answered > 0 || suppliers.some((supplier) => supplier.status !== 'not_started')
    ? 'in_progress'
    : 'not_started';
};
